<template>
  <div class="container-fluid p-4 accommodation-page">
    <div class="page-header">
      <h1 class="page-title">
        {{ $t('accommodationSummary.title') }}
        <span class="d-inline-block" tabindex="0" data-bs-toggle="popover" data-bs-trigger="hover focus" :data-bs-content="$t('accommodationSummary.help')">
          <i class="bi bi-question-circle ms-2 help-icon"></i>
        </span>
      </h1>
      <div class="header-controls">
        <DateRangePicker v-model="dateRange" alignment="right" :max-days="31" :min-date="minDate" :max-date="maxDate" />
        <a href="#" class="reset-link" @click.prevent="resetDateRange">
          <i class="bi bi-arrow-counterclockwise me-1"></i>{{ $t('common.resetView') }}
        </a>
      </div>
    </div>

    <!-- Guest trend -->
    <div class="card trend-card">
      <div class="trend-header">
        <h3 class="card-title">{{ $t('accommodationSummary.bookings') }}</h3>
        <span class="trend-subtitle">{{ $t('accommodationSummary.bookingsSubtitle') }}</span>
      </div>
      <div class="trend-total">
        {{ data ? data.total_guests.toLocaleString() : '-' }}<span class="trend-total-unit">{{ $t('accommodationSummary.peopleUnit') }}</span>
      </div>
      <TrendBarChart
        :labels="data?.trend_chart?.labels ?? []"
        :thisYearData="data?.trend_chart?.this_year_data ?? []"
        :lastYearData="data?.trend_chart?.last_year_data ?? []"
        :currentLabel="$t('accommodationSummary.selectedPeriod')"
        :previousLabel="$t('accommodationSummary.previousYearPeriod')"
      />
    </div>

    <!-- KPI cards -->
    <div class="row g-3 mt-1">
      <div class="col-md-4">
        <KpiCard
          :title="$t('accommodationSummary.occupancy')"
          :value="data?.kpis?.occupancy?.rate ?? null"
          :trend="data?.kpis?.occupancy?.trend ?? null"
          :trendDirection="data?.kpis?.occupancy?.trend_direction ?? null"
        />
      </div>
      <div class="col-md-4">
        <KpiCard
          :title="$t('accommodationSummary.repeaters')"
          :value="data?.kpis?.repeater?.rate ?? null"
          :trend="data?.kpis?.repeater?.trend ?? null"
          :trendDirection="data?.kpis?.repeater?.trend_direction ?? null"
        />
      </div>
      <div class="col-md-4">
        <KpiCard
          :title="$t('accommodationSummary.stockholders')"
          :value="data?.kpis?.stockholder?.rate ?? null"
          :trend="data?.kpis?.stockholder?.trend ?? null"
          :trendDirection="data?.kpis?.stockholder?.trend_direction ?? null"
        />
      </div>
    </div>

    <!-- Demographics -->
    <div class="row g-3 mt-1">
      <div class="col-md-6">
        <DonutLegendCard :title="$t('accommodationSummary.ageGroups')" :items="ageGroupItems" />
      </div>
      <div class="col-md-6">
        <DonutLegendCard :title="$t('accommodationSummary.genders')" :items="genderItems" />
      </div>
    </div>

    <!-- Residential areas -->
    <div class="row g-3 mt-1 mb-3">
      <div class="col-12">
        <ResidenceBarCard
          :title="$t('accommodationSummary.residentialAreas')"
          :areas="data?.residential_areas ?? []"
          :emptyLabel="$t('accommodationSummary.noData')"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Popover } from 'bootstrap';
import axios from 'axios';
import { colors } from '../../../../consts/colors';
import DateRangePicker from '../../../components/user/dashboard/DateRangePicker.vue';
import KpiCard from '../../../components/user/statistics/KpiCard.vue';
import DonutLegendCard from '../../../components/user/statistics/DonutLegendCard.vue';
import ResidenceBarCard from '../../../components/user/statistics/ResidenceBarCard.vue';
import TrendBarChart from '../../../components/user/statistics/TrendBarChart.vue';
import { fetchDateRangeLimits } from '../../../utils/statisticDataUtils';
import { useSharedDateRange, defaultDateRange } from '../../../composables/useSharedDateRange';

const { t: $t } = useI18n();

const data = ref(null);
const minDate = ref(null);
const maxDate = ref(null);

// Shared across dashboard, events and accommodations
const { dateRange } = useSharedDateRange();

const ageGroupDefinitions = [
  { key: 'less_20', labelKey: 'guestDemographics.age_less_20', color: colors.blue[500] },
  { key: '20', labelKey: 'guestDemographics.age_20', color: colors.blue[400] },
  { key: '30', labelKey: 'guestDemographics.age_30', color: colors.blue[300] },
  { key: '40', labelKey: 'guestDemographics.age_40', color: colors.blue[200] },
  { key: '50', labelKey: 'guestDemographics.age_50', color: colors.blue[100] },
  { key: 'more_60', labelKey: 'guestDemographics.age_more_60', color: colors.blue[50] },
  { key: 'other', labelKey: 'guestDemographics.age_other', color: colors.gray[200] },
];

const genderDefinitions = [
  { key: 'male', labelKey: 'guestDemographics.male', color: colors.blue[500] },
  { key: 'female', labelKey: 'guestDemographics.female', color: colors.blue[400] },
  { key: 'other', labelKey: 'guestDemographics.no_answer', color: colors.blue[300] },
];

const ageGroupItems = computed(() => ageGroupDefinitions.map(def => ({
  key: def.key,
  label: $t(def.labelKey),
  value: data.value?.age_groups?.find(item => item.age_group === def.key)?.percentage ?? 0,
  color: def.color,
})));

const genderItems = computed(() => genderDefinitions.map(def => ({
  key: def.key,
  label: $t(def.labelKey),
  value: data.value?.genders?.find(item => item.gender === def.key)?.percentage ?? 0,
  color: def.color,
})));

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fetchSummary = async () => {
  if (!dateRange.value?.startDate || !dateRange.value?.endDate) {
    return;
  }

  try {
    const response = await axios.get('/api/user/statistics/dashboard/accommodation-summary', {
      params: {
        start_date: formatDate(dateRange.value.startDate),
        end_date: formatDate(dateRange.value.endDate),
      }
    });
    data.value = response.data;
  } catch (error) {
    console.error('Error fetching accommodation summary:', error);
  }
};

/**
 * 期間をデフォルト(今日〜7日後)に戻す
 */
const resetDateRange = () => {
  dateRange.value = defaultDateRange();
};

onMounted(() => {
  document.querySelectorAll('[data-bs-toggle="popover"]').forEach((el) => new Popover(el));

  // Fetch the summary and the picker limits in parallel
  fetchSummary();
  fetchDateRangeLimits().then((limits) => {
    if (limits) {
      minDate.value = limits.minDate;
      maxDate.value = limits.maxDate;
    }
  });
});

watch(dateRange, () => {
  fetchSummary();
}, { deep: true });
</script>

<style lang="scss" scoped>
.accommodation-page {
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;

    .page-title {
      font-size: 24px;
      font-weight: 700;
      color: v-bind('colors.textPrimary');
      margin: 0;
      white-space: nowrap;
      flex-shrink: 0;

      .help-icon {
        font-size: 18px;
        color: v-bind('colors.textLight');
        cursor: pointer;
      }
    }

    .header-controls {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 12px;

      .reset-link {
        font-size: 13px;
        color: v-bind('colors.btnBgColorPrimary');
        text-decoration: none;
        white-space: nowrap;
      }
    }
  }

  .trend-card {
    background: v-bind('colors.white');
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px v-bind('colors.shadowColor');
    border: none;

    .trend-header {
      display: flex;
      align-items: baseline;
      gap: 12px;

      .card-title {
        font-size: 18px;
        font-weight: 600;
        color: v-bind('colors.textPrimary');
        margin: 0;
      }

      .trend-subtitle {
        font-size: 12px;
        color: v-bind('colors.gray[500]');
      }
    }

    .trend-total {
      font-size: 28px;
      font-weight: 700;
      color: v-bind('colors.textPrimary');
      margin: 4px 0 8px;

      .trend-total-unit {
        font-size: 14px;
        font-weight: 500;
        margin-left: 2px;
        color: v-bind('colors.gray[500]');
      }
    }
  }
}
</style>
